<template>
  <div class="africa-map-wrapper">
    <div class="africa-map-legend" v-if="hasRealData">
      <div class="legend-title">Tenders by Country</div>
      <div class="legend-scale d-flex align-items-center">
        <span class="legend-label me-2">Low</span>
        <div class="legend-bar flex-grow-1"></div>
        <span class="legend-label ms-2">High</span>
      </div>
    </div>
    <div ref="chartEl" class="africa-map-chart"></div>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'

// Lightweight wrapper around Google GeoChart to render an Africa map.
// Expects props.data in the shape returned by bd-analytics countryPerformance:
// [{ country, totalOpportunities, wins, winRate, pipelineValue }, ...]

const GOOGLE_LOADER_SRC = 'https://www.gstatic.com/charts/loader.js'

function loadGoogleCharts() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Window is undefined'))
    }

    if (window.google && window.google.charts && window.google.visualization) {
      window.google.charts.load('current', { packages: ['geochart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
      return
    }

    const existing = document.querySelector(`script[src="${GOOGLE_LOADER_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => {
        window.google.charts.load('current', { packages: ['geochart'] })
        window.google.charts.setOnLoadCallback(() => resolve(window.google))
      })
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Charts')))
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_LOADER_SRC
    script.async = true
    script.onload = () => {
      if (!window.google || !window.google.charts) {
        reject(new Error('Google Charts not available'))
        return
      }
      window.google.charts.load('current', { packages: ['geochart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
    }
    script.onerror = () => reject(new Error('Failed to load Google Charts'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'AfricaMap',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    metric: {
      type: String,
      default: 'pipelineValue', // or 'totalOpportunities'
    },
    title: {
      type: String,
      default: 'Tenders by Country (Africa)',
    },
  },
  setup(props) {
    const chartEl = ref(null)
    const chart = ref(null)
    const googleRef = ref(null)

    const hasRealData = computed(
      () => Array.isArray(props.data) && props.data.length > 0,
    )

    const buildDataTable = () => {
      if (!googleRef.value) return null
      const google = googleRef.value
      const dataTable = new google.visualization.DataTable()
      dataTable.addColumn('string', 'Country')
      dataTable.addColumn('number', props.metric === 'totalOpportunities' ? 'Opportunities' : 'Pipeline')

      const rows = hasRealData.value
        ? props.data.map((row) => {
            const value = Number(row[props.metric] ?? row.pipelineValue ?? row.totalOpportunities ?? 0)
            return [row.country || 'Unknown', isNaN(value) ? 0 : value]
          })
        : [['Africa', 0]]
      dataTable.addRows(rows)
      return dataTable
    }

    const drawChart = async () => {
      if (!chartEl.value) return

      try {
        if (!googleRef.value) {
          googleRef.value = await loadGoogleCharts()
        }
        const google = googleRef.value
        const dataTable = buildDataTable()
        if (!dataTable) return

        if (!chart.value) {
          chart.value = new google.visualization.GeoChart(chartEl.value)
        }

        const options = {
          region: '002', // Africa
          displayMode: 'regions',
          legend: { textStyle: { fontSize: 10 } },
          // Blue to maroon range
          colorAxis: { colors: ['#1e88e5', '#5e35b1', '#800000'] },
          backgroundColor: '#ffffff',
          datalessRegionColor: '#f5f5f5',
          defaultColor: '#f5f5f5',
        }

        chart.value.draw(dataTable, options)
      } catch (e) {
        // Fail silently; map is purely decorative
        // eslint-disable-next-line no-console
        console.error('Failed to render Africa map', e)
      }
    }

    onMounted(() => {
      drawChart()
    })

    watch(
      () => [props.data, props.metric],
      () => {
        drawChart()
      },
      { deep: true },
    )

    onBeforeUnmount(() => {
      chart.value = null
    })

    return {
      chartEl,
      hasRealData,
    }
  },
}
</script>

<style scoped>
.africa-map-wrapper {
  width: 100%;
  min-height: 360px;
}

.africa-map-legend {
  margin-bottom: 8px;
}

.legend-title {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 2px;
}

.legend-scale {
  font-size: 11px;
}

.legend-bar {
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, #1e88e5 0%, #5e35b1 50%, #800000 100%);
}

.legend-label {
  white-space: nowrap;
}

.africa-map-chart {
  width: 100%;
  height: 360px;
}
</style>
